import { Injectable } from '@angular/core';
import {
  IpoLanguageService,
  IpoStringService,
} from '@ip-only/front-end-library';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { DefinitionsApiService } from '../api/definitions-api.service';

export interface Translation {
  language: string;
  name: string;
}

export interface ConstructionDefinition {
  id: number;
  name: string;
  translations: Translation[];
}

const unknownDefinition = 'definition.unknown';
const unknownTranslation = 'definition.noTranslation';

@Injectable({ providedIn: 'any' })
export class DefinitionsService {
  private accommodationMethods$ = new BehaviorSubject<ConstructionDefinition[]>(
    [],
  );
  private permissionTypes$ = new BehaviorSubject<ConstructionDefinition[]>([]);

  constructor(
    private api: DefinitionsApiService,
    private languageService: IpoLanguageService,
    private stringService: IpoStringService,
  ) {}

  getAccommodationMethodTranslations$(): Observable<
    { id: number; translation: string }[]
  > {
    return combineLatest([
      this.accommodationMethods$.asObservable(),
      this.languageService.getLanguage$(),
    ]).pipe(
      map(([accMethods, language]) =>
        this.translateDefinitions(accMethods, language),
      ),
    );
  }

  getAccommodationMethodTranslation(id: number, language: string) {
    return this.getDefinitionTranslation(
      id,
      language,
      this.accommodationMethods$.getValue(),
    );
  }

  getPermissionTypeTranslation(id: number, language: string) {
    return this.getDefinitionTranslation(
      id,
      language,
      this.permissionTypes$.getValue(),
    );
  }

  fetchDefinitions() {
    this.api
      .fetchDefinitions('accommodation-methods')
      .subscribe((definitions) => this.accommodationMethods$.next(definitions));
    this.api
      .fetchDefinitions('permission-types')
      .subscribe((definitions) => this.permissionTypes$.next(definitions));
  }

  private translateDefinitions(
    definitions: ConstructionDefinition[],
    language: string,
  ) {
    return definitions.map((definition) => {
      return {
        id: definition.id,
        translation: this.getTranslation(definition.translations, language),
      };
    });
  }

  private getDefinitionTranslation(
    id: number,
    language: string,
    definitions: ConstructionDefinition[],
  ): string {
    const definition = definitions.find((_definition) => _definition.id === id);
    if (!definition) {
      return this.stringService.getText(unknownDefinition);
    }
    return this.getTranslation(definition.translations, language);
  }

  private getTranslation(
    translations: Translation[],
    language: string,
  ): string {
    const translation = translations.find(
      (_translation) => _translation.language === language,
    );
    return translation
      ? translation.name
      : this.stringService.getText(unknownTranslation);
  }
}
